import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction } from '@lyearn/i18n';
import {
  BriefcaseOutline,
  CheckmarkOutline,
  CornerDownLeftOutline,
  DiamondOutline,
  Edit2Outline,
  HeartOutline,
  Menu2Outline,
  Menu3Outline,
  MessageSquare2Outline,
  RefreshOutline,
  Swap2Outline,
  Trash2Outline,
} from '@lyearn/icons';
import { ActionMenuItemType } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { TextUseCaseEnum } from '@/types/schema';

import { aiMenuIconFill } from '../styles';
import { AIEditorActionTypes, AIEditorState } from '../types';

const ICON_SIZE = 'w-20 h-20 ms-12';

type getAIEditorMenuItemsParams = {
  editorState: AIEditorState;
  t: TFunction;
  textUseCase: TextUseCaseEnum;
};

export const getAIEditorMenuItems = ({
  editorState,
  t,
  textUseCase,
}: getAIEditorMenuItemsParams): ActionMenuItemType[] => {
  const showTryAgainSection = [
    AIEditorState.EmptyCompletion,
    AIEditorState.DraftCompletion,
  ].includes(editorState);

  const showLengthSection = [
    AIEditorState.EmptyCompletion,
    AIEditorState.DraftInput,
    AIEditorState.DraftCompletion,
  ].includes(editorState);

  const showToneSection =
    ([
      TextUseCaseEnum.EventDescription,
      TextUseCaseEnum.OkrDescription,
      TextUseCaseEnum.ContentDescription,
      TextUseCaseEnum.CourseDescrtiption,
      TextUseCaseEnum.PathDescription,
      TextUseCaseEnum.QuizDescription,
    ].includes(textUseCase) &&
      [AIEditorState.EmptyCompletion, AIEditorState.DraftInput].includes(editorState)) ||
    ([TextUseCaseEnum.UserFeedback, TextUseCaseEnum.UserPraise].includes(textUseCase) &&
      [AIEditorState.DraftInput, AIEditorState.DraftCompletion].includes(editorState)) ||
    ([TextUseCaseEnum.UserFeedbackRequest, TextUseCaseEnum.PerformanceFeedback].includes(
      textUseCase,
    ) &&
      [AIEditorState.DraftInput].includes(editorState));

  const showDiscardSection = [
    AIEditorState.EmptyCompletion,
    AIEditorState.DraftCompletion,
  ].includes(editorState);

  const allItems: (ActionMenuItemType & { enabled: boolean })[] = [
    {
      type: 'item',
      label: t('AI:WriteWithLyearnAI'),
      id: AIEditorActionTypes.WriteWithLyearnAI,
      EndIcon: (props) => <Edit2Outline {...props} className={cn(aiMenuIconFill, ICON_SIZE)} />,
      enabled: editorState === AIEditorState.DraftInput,
    },
    {
      type: 'item',
      label: t('AI:Keep'),
      id: AIEditorActionTypes.Keep,
      EndIcon: (props) => <CheckmarkOutline {...props} className={cn(aiMenuIconFill, ICON_SIZE)} />,
      enabled: editorState === AIEditorState.EmptyCompletion,
    },
    {
      type: 'item',
      label: t('AI:ReplaceSelection'),
      id: AIEditorActionTypes.ReplaceSelection,
      EndIcon: (props) => <Swap2Outline {...props} className={cn(aiMenuIconFill, ICON_SIZE)} />,
      enabled: editorState === AIEditorState.DraftCompletion,
    },
    {
      type: 'item',
      label: t('AI:InsertBelow'),
      id: AIEditorActionTypes.InsertBelow,
      EndIcon: (props) => (
        <CornerDownLeftOutline {...props} className={cn(aiMenuIconFill, ICON_SIZE)} />
      ),
      enabled: editorState === AIEditorState.DraftCompletion,
    },
    {
      type: 'item',
      label: t('AI:TryAgain'),
      id: AIEditorActionTypes.TryAgain,
      EndIcon: (props) => <RefreshOutline {...props} className={cn(aiMenuIconFill, ICON_SIZE)} />,
      enabled: showTryAgainSection,
    },
    {
      type: 'label',
      label: t('AI:Length'),
      enabled: showLengthSection,
    },
    {
      type: 'item',
      label: t('AI:MakeLonger'),
      id: AIEditorActionTypes.MakeLonger,
      EndIcon: (props) => <Menu3Outline {...props} className={cn(aiMenuIconFill, ICON_SIZE)} />,
      enabled:
        showLengthSection &&
        !(
          [TextUseCaseEnum.UserFeedbackRequest].includes(textUseCase) &&
          [AIEditorState.DraftCompletion].includes(editorState)
        ),
    },
    {
      type: 'item',
      label: t('AI:MakeShorter'),
      id: AIEditorActionTypes.MakeShorter,
      EndIcon: (props) => <Menu2Outline {...props} className={cn(aiMenuIconFill, ICON_SIZE)} />,
      enabled: showLengthSection,
    },
    {
      type: 'label',
      label: t('AI:Tone'),
      enabled: showToneSection,
    },
    {
      type: 'item',
      label: t('AI:Professional'),
      id: AIEditorActionTypes.Professional,
      EndIcon: (props) => <BriefcaseOutline {...props} className={cn(aiMenuIconFill, ICON_SIZE)} />,
      enabled: showToneSection,
    },
    {
      type: 'item',
      label: t('AI:Casual'),
      id: AIEditorActionTypes.Casual,
      EndIcon: (props) => (
        <MessageSquare2Outline {...props} className={cn(aiMenuIconFill, ICON_SIZE)} />
      ),
      enabled:
        showToneSection &&
        ![TextUseCaseEnum.EventDescription, TextUseCaseEnum.OkrDescription].includes(textUseCase),
    },
    {
      type: 'item',
      label: t('AI:Confident'),
      id: AIEditorActionTypes.Confident,
      EndIcon: (props) => <DiamondOutline {...props} className={cn(aiMenuIconFill, ICON_SIZE)} />,
      enabled: showToneSection,
    },
    {
      type: 'item',
      label: t('AI:Friendly'),
      id: AIEditorActionTypes.Friendly,
      EndIcon: (props) => <HeartOutline {...props} className={cn(aiMenuIconFill, ICON_SIZE)} />,
      enabled:
        showToneSection &&
        ![TextUseCaseEnum.EventDescription, TextUseCaseEnum.OkrDescription].includes(textUseCase),
    },
    {
      type: 'divider',
      enabled: showDiscardSection,
    },
    {
      type: 'item',
      label: t('AI:Discard'),
      id: AIEditorActionTypes.Discard,
      EndIcon: (props) => (
        <Trash2Outline {...props} className={ICON_SIZE} fillColorToken="icon-error" />
      ),
      appearance: 'danger',
      enabled: showDiscardSection,
    },
  ];

  return getEnabledItems<ActionMenuItemType>(allItems);
};
